import { useMutation, useQuery } from '@tanstack/react-query';
import { QueryKeys } from '@/types/query';
import * as ClientService from '@/services/client';
import { toRQMutation, toRQQuery } from '@/utilities/service';
import { adserMapper } from '@/utilities/client';
import { IFormAdser } from '@vestiaire/vc-address-form/src/interfaces/address';
import { AxiosError } from 'axios';
import { ServerErrorOne, ServerErrorTwo } from '@/types/service';
import { useErrorHandling } from './common';

const DEFAULT_STALE_CACHE_TIME = 5 * 60 * 1000;
const DEFAULT_CACHE_CONFIG = {
  staleTime: DEFAULT_STALE_CACHE_TIME,
  cacheTime: DEFAULT_STALE_CACHE_TIME,
};

export function useClientId(id: number, enabled?: boolean) {
  const { remove, isFetching, isRefetching, data, refetch } = useQuery({
    queryKey: [QueryKeys.CLIENT_ID, id],
    queryFn: () => ClientService.getClientById(id),
    ...DEFAULT_CACHE_CONFIG,
    enabled: enabled != null ? enabled : id > 0,
  });

  return toRQQuery(data, isFetching, isRefetching, refetch, remove);
}

export function useClientEmail(email: string, enabled?: boolean) {
  const { remove, isFetching, isRefetching, data, refetch } = useQuery({
    queryKey: [QueryKeys.CLIENT_EMAIL, email],
    queryFn: () => ClientService.getClientByEmail(email),
    ...DEFAULT_CACHE_CONFIG,
    enabled: enabled != null ? enabled : email !== '',
  });

  return toRQQuery(data, isFetching, isRefetching, refetch, remove);
}

export function useClientPhoneNumber(phoneNumber: string, enabled?: boolean) {
  const { remove, isFetching, isRefetching, data, refetch } = useQuery({
    queryKey: [QueryKeys.CLIENT_PHONE_NUMBER, phoneNumber],
    queryFn: () => ClientService.getClientByPhoneNumber(phoneNumber),
    staleTime: 1000,
    cacheTime: 1000,
    enabled: enabled != null ? enabled : phoneNumber !== '',
  });

  return toRQQuery(data, isFetching, isRefetching, refetch, remove);
}

export function useClientSiftScoreById(id: number, enabled?: boolean) {
  const { remove, isFetching, isRefetching, data, refetch } = useQuery({
    queryKey: [QueryKeys.SIFT_SCORE, id],
    queryFn: () => ClientService.getClientSiftScoreById(id),
    ...DEFAULT_CACHE_CONFIG,
    enabled: enabled != null ? enabled : id > 0,
  });

  return toRQQuery(data, isFetching, isRefetching, refetch, remove);
}

export function useUpdateEmail(id: number) {
  const { isLoading, data, mutate } = useMutation({
    mutationFn: (data: ClientService.UpdateEmailData) => ClientService.updateEmail(id, data),
  });

  return toRQMutation(data, isLoading, mutate);
}

export function useTriggerGetPaidManualVerification(clientId: string) {
  const { getErrorMsgFromResponse } = useErrorHandling();

  const mutation = useMutation<unknown, AxiosError<ServerErrorOne>, ClientService.GetPaidConfirmationCodeData>({
    mutationFn: (data: ClientService.GetPaidConfirmationCodeData) =>
      ClientService.triggerGetPaidManualVerification(clientId, data),
    ...DEFAULT_CACHE_CONFIG,
  });
  const errorMessage = mutation.error ? getErrorMsgFromResponse(mutation.error) : undefined;

  return { ...mutation, errorMessage };
}

export function useClientActionHistos(id: number, page: number, pageSize: number) {
  return useQuery({
    queryKey: [QueryKeys.CLIENT_ACTION_HISTOS, id, page, pageSize],
    queryFn: () => ClientService.getClientActionHistos(id, page, pageSize),
    ...DEFAULT_CACHE_CONFIG,
  });
}

export function useClientBadges(id: number) {
  const { remove, isFetching, isRefetching, data, refetch } = useQuery({
    queryKey: [QueryKeys.CLIENT_BADGES, id],
    queryFn: () => ClientService.getClientBadges(id),
    ...DEFAULT_CACHE_CONFIG,
  });

  return toRQQuery(data, isFetching, isRefetching, refetch, remove);
}

export function useClientSalesReports(customerId: string) {
  return useQuery({
    queryKey: [QueryKeys.CLIENT_SALES_REPORTS, customerId],
    queryFn: () => ClientService.getClientSalesReports(customerId),
    ...DEFAULT_CACHE_CONFIG,
    enabled: !!customerId,
  });
}

export function useClientConnect(id: number) {
  const { isLoading, data, mutate } = useMutation({
    mutationFn: (clientType?: 'buyer' | 'seller') => ClientService.clientConnect(id, clientType),
  });

  return toRQMutation(data, isLoading, mutate);
}

export function useClientChatModerateStats(id: number, enabled?: boolean) {
  const { remove, isFetching, isRefetching, data, refetch } = useQuery({
    queryKey: [QueryKeys.CLIENT_CHAT_MODERATE_STATS, id],
    queryFn: () => ClientService.getClientChatModerateStats(id),
    ...DEFAULT_CACHE_CONFIG,
    enabled: enabled != null ? enabled : id > 0,
  });

  return toRQQuery(data, isFetching, isRefetching, refetch, remove);
}

export function useClientCanceledSales(id: number, page: number, pageSize: number, enabled?: boolean) {
  return useQuery({
    queryKey: [QueryKeys.CANCELED_SALES, id, page, pageSize],
    queryFn: () => ClientService.getClientCanceledSalesById(id, page, pageSize),
    ...DEFAULT_CACHE_CONFIG,
    enabled: enabled != null ? enabled : id > 0,
  });
}

export function useClientShippingFromAddress(
  customerId: string,
  clientShippingAddressId?: number | null,
  enabled?: boolean,
) {
  const { remove, isFetching, isRefetching, data, refetch } = useQuery({
    queryKey: [QueryKeys.SHIPPING_FROM_ADDRESS, customerId, clientShippingAddressId],
    queryFn: () => ClientService.getClientShippingFromAddress(customerId, clientShippingAddressId),
    ...DEFAULT_CACHE_CONFIG,
    enabled: !!clientShippingAddressId && !!customerId && enabled,
  });

  const query = toRQQuery(data, isFetching, isRefetching, refetch, remove);

  // No choice - This legacy
  // eslint-disable-next-line
  const clientAddress = data?.kind === 'Ok' && data.data?.data ? adserMapper(data.data.data as IFormAdser) : null;

  return { ...query, data: clientAddress };
}

export function useClientStats(id: number) {
  const { remove, isFetching, isRefetching, data, refetch } = useQuery({
    queryKey: [QueryKeys.CLIENT_STATS, id],
    queryFn: () => ClientService.getClientStats(id),
    ...DEFAULT_CACHE_CONFIG,
  });

  return toRQQuery(data, isFetching, isRefetching, refetch, remove);
}

export function useClientLastLoginAttempt(loginId: string) {
  const { remove, isFetching, isRefetching, data, refetch } = useQuery({
    queryKey: [QueryKeys.LAST_LOGIN_ATTEMPT, loginId],
    queryFn: () => ClientService.getClientLastLoginAttempt(loginId),
    ...DEFAULT_CACHE_CONFIG,
    enabled: false,
  });

  return toRQQuery(data, isFetching, isRefetching, refetch, remove);
}

export function useRemoveCancellation() {
  return useMutation<unknown, AxiosError<ServerErrorTwo>, { productId: number; clientId: number }>({
    mutationFn: ({ productId, clientId }) => ClientService.removeCancellation(productId, clientId),
    ...DEFAULT_CACHE_CONFIG,
  });
}

export function useShippedInTime() {
  return useMutation<unknown, AxiosError<ServerErrorTwo>, { productId: number; clientId: number }>({
    mutationFn: ({ productId, clientId }) => ClientService.shippedInTimeItem(productId, clientId),
    ...DEFAULT_CACHE_CONFIG,
  });
}

export function useDeleteVerifiedPhone() {
  const { getErrorMsgFromResponse } = useErrorHandling();

  const mutation = useMutation<unknown, AxiosError<ServerErrorOne>, { clientId: string }>({
    mutationFn: ({ clientId }) => ClientService.deleteVerifiedPhone(clientId),
    ...DEFAULT_CACHE_CONFIG,
  });

  const errorMessage = mutation.error ? getErrorMsgFromResponse(mutation.error) : undefined;

  return { ...mutation, errorMessage };
}

export function useClientReliabilityStats(id: number, enabled?: boolean) {
  const { remove, isFetching, isRefetching, data, refetch } = useQuery({
    queryKey: [QueryKeys.CLIENT_RELIABILITY_STATS, id],
    queryFn: () => ClientService.getClientReliabilityStats(id),
    ...DEFAULT_CACHE_CONFIG,
    enabled: enabled != null ? enabled : id > 0,
  });

  return toRQQuery(data, isFetching, isRefetching, refetch, remove);
}

export function usePersonaKycHistos(id: number, page: number, pageSize: number, enabled: boolean) {
  return useQuery({
    queryKey: [QueryKeys.PERSONA_KYC_HISTOS, id, page, pageSize],
    queryFn: () => ClientService.getPersonaKyc(id, page, pageSize),
    enabled,
    ...DEFAULT_CACHE_CONFIG,
  });
}
